﻿import { useContext, useEffect, useState } from "react";
import { CopyToClipboard } from "react-copy-to-clipboard";
import { useCompactListLayout } from "@/utils/hooks/useCompactListLayout";
import { useFeaturePermission } from "@/utils/hooks/useFeaturePermission";
import { Button } from "@/components/ui/button";
import { Spinner } from "@/components/ui/spinner";
import { BUTTONS } from "@/utils/constants/appConstants/commonConstants";
import { OrganizationContext } from "@/context/providers/OrganizationProvider";
import { fetchInvites, resendInvitation } from "@/services/user.service";
import { INVITED } from "@/utils/constants/constants";
import { SOMETHING_WENT_WRONG } from "@/utils/constants/messages";
import { FEATURE_PERMISSIONS } from "@/utils/constants/featurePermissions";
import { errorToasterMessage, successToasterMessage } from "@/utils/functions/toaster";
import { getListCacheBootstrap, writeListCache } from "@/utils/listPageCache";
import { ReloadButton } from "@/components/buttons/ReloadButton";
import { CardTitle } from "@/components/cards/CardTitle";
import {
    InviteUserTableSkeletonDesktop,
    InviteUserTableSkeletonMobile,
} from "@/components/skeletons";

const InviteUserTable = () => {
    const invitesBootstrap = getListCacheBootstrap("invite-users", {});

    const [invites, setInvites] = useState<any>(invitesBootstrap.items);
    const [isLoading, setIsLoading] = useState(!invitesBootstrap.hasCache);
    const [copy, setCopy] = useState<string | number>("");
    const [loadingIds, setLoadingIds] = useState<any>([]);
    const isCompactLayout = useCompactListLayout();
    const organization = useContext<any>(OrganizationContext);

    const isViewInvitedUserPermitted = useFeaturePermission(FEATURE_PERMISSIONS.AdminPortalViewInvitedUser);

    const loadInvites = (options?: { showSkeleton?: boolean }) => {
        const { cacheKey, cached } = getListCacheBootstrap("invite-users", {});

        if (!cached || options?.showSkeleton) {
            setIsLoading(true);
            setInvites([]);
        }

        const invite_status = "invited";
        fetchInvites(
            invite_status,
            (response: any) => {
                const nextInvites = response.invitations ?? [];
                writeListCache(cacheKey, nextInvites, nextInvites.length);
                setIsLoading(false);
                setInvites(nextInvites);
            },
            (error: any) => {
                setIsLoading(false);
                console.log(error);
            }
        );
    };

    useEffect(() => {
        loadInvites();
    }, [organization]);

    const handleResendInvitation = (inviteId: string) => {
        setLoadingIds((prevLoadingIds: any) => [...prevLoadingIds, inviteId]);
        const data = [inviteId];
        resendInvitation(
            JSON.stringify(data),
            (response: any) => {
                setLoadingIds((prevLoadingIds: any[]) =>
                    prevLoadingIds.filter((id: string) => id !== inviteId)
                );
                successToasterMessage(response?.invitations[0].message);
            },
            (error: any) => {
                setLoadingIds((prevLoadingIds: any[]) =>
                    prevLoadingIds.filter((id: string) => id !== inviteId)
                );
                errorToasterMessage(SOMETHING_WENT_WRONG);
                console.log(error);
            }
        );
    };

    const getInviteStatusBadgeClass = (status: string) =>
        `badge badge-sm badge-dim ${status === INVITED ? "badge-outline-primary" : "badge-outline-success"}`;

    if (!isViewInvitedUserPermitted) {
        return null;
    }

    const renderResendButton = (inviteId: string, id: string) => {
        const isResending = loadingIds.includes(inviteId);
        return (
            <Button
                id={id}
                type="button"
                disabled={isResending}
                onClick={() => handleResendInvitation(inviteId)}
            >
                {isResending ? (
                    <Spinner id={`${id}-loader`} size="sm" />
                ) : (
                    BUTTONS.RESEND
                )}
            </Button>
        );
    };

    return (
        <div className="gs-dashboard-card">
            <CardTitle
                options={
                    <ReloadButton
                        id="dashboard-recent-invite-table"
                        onClick={() => loadInvites({ showSkeleton: true })}
                    />
                }
            >
                <h6 className="title" id="dashboard-recent-invite-table-heading">
                    Recently Invited Users
                </h6>
            </CardTitle>
            <div className="gs-dashboard-table-wrap">
                {!isCompactLayout ? (
                    <table className="gs-dashboard-table">
                        <thead>
                            <tr>
                                <th
                                    id="dashboard-recent-invite-email-table-head"
                                    className="table-th gs-dashboard-table-col-email"
                                >
                                    Email
                                </th>
                                <th
                                    id="dashboard-recent-invite-status-table-head"
                                    className="table-th gs-dashboard-table-col-status"
                                >
                                    Status
                                </th>
                                <th
                                    id="dashboard-recent-invite-invite-link-table-head"
                                    className="table-th gs-dashboard-table-col-link"
                                >
                                    Invite Links
                                </th>
                                <th className="table-th gs-dashboard-table-col-action" />
                            </tr>
                        </thead>
                        <tbody>
                            {!isLoading ? (
                                invites.length > 0 ? (
                                    invites.map((invite: any, index: string) => (
                                        <tr key={index} className="pe-auto">
                                            <td className="table-td gs-dashboard-table-col-email">
                                                <div className="gs-dashboard-table-email">
                                                    <div className="gs-dashboard-table-email-icon">
                                                        <em
                                                            className="icon ni ni-user icons"
                                                            id={`recent-invite-table-icon-${index}`}
                                                        />
                                                    </div>
                                                    <span
                                                        className="tb-lead"
                                                        id={`recent-invite-table-email-${index}`}
                                                    >
                                                        {invite?.email_address}
                                                    </span>
                                                </div>
                                            </td>
                                            <td className="table-td gs-dashboard-table-col-status">
                                                <span
                                                    id={`recent-invite-table-status-${index}`}
                                                    className={getInviteStatusBadgeClass(invite?.status)}
                                                >
                                                    {invite?.status === INVITED
                                                        ? "Invite Pending"
                                                        : "Invite accepted"}
                                                </span>
                                            </td>
                                            <td className="table-td gs-dashboard-table-col-link">
                                                {invite?.status === INVITED && (
                                                    <CopyToClipboard
                                                        text={invite?.invitation_link || ""}
                                                        onCopy={() => setCopy(index)}
                                                    >
                                                        <a
                                                            href="#home"
                                                            onClick={(e) => e.preventDefault()}
                                                        >
                                                            <span
                                                                id={`recent-invite-table-copy-link-${index}`}
                                                                className="gs-dashboard-invite-link"
                                                            >
                                                                {copy !== index
                                                                    ? "Copy Invite Link"
                                                                    : "Copied"}
                                                            </span>
                                                        </a>
                                                    </CopyToClipboard>
                                                )}
                                            </td>
                                            <td
                                                className="table-td gs-dashboard-table-col-action gs-dashboard-table-action"
                                                id={`recent-invite-table-action-${index}`}
                                            >
                                                {renderResendButton(
                                                    invite.id,
                                                    `recent-invite-table-resend-invite-btn-${index}`
                                                )}
                                            </td>
                                        </tr>
                                    ))
                                ) : (
                                    <tr>
                                        <td colSpan={4}>
                                            <div className="p-4 text-center">
                                                <span id="recent-invite-table-no-data-msg">
                                                    No invites yet
                                                </span>
                                            </div>
                                        </td>
                                    </tr>
                                )
                            ) : (
                                <InviteUserTableSkeletonDesktop />
                            )}
                        </tbody>
                    </table>
                ) : (
                    <div>
                        {!isLoading ? (
                            invites.length > 0 ? (
                                invites.map((invite: any, index: string) => (
                                    <div className="gs-users-mobile-card" key={index}>
                                        <div className="gs-mobile-card-icon">
                                            <em
                                                className="icon ni ni-user"
                                                id={`recent-invite-table-icon-${index}-mb`}
                                            />
                                        </div>
                                        <div className="gs-users-mobile-meta">
                                            <div>
                                                <div
                                                    className="gs-users-table-user-email"
                                                    id={`recent-invite-table-email-${index}-mb`}
                                                >
                                                    {invite?.email_address}
                                                </div>
                                                <div className="meta mt-2">
                                                    <span
                                                        id={`recent-invite-table-status-${index}-mb`}
                                                        className={getInviteStatusBadgeClass(invite?.status)}
                                                    >
                                                        {invite?.status === INVITED
                                                            ? "Invite Pending"
                                                            : "Invite accepted"}
                                                    </span>
                                                </div>
                                                <div className="mt-2">
                                                    {invite?.status === INVITED && (
                                                        <CopyToClipboard
                                                            text={invite?.invitation_link || ""}
                                                            onCopy={() => setCopy(index)}
                                                        >
                                                            <a
                                                                href="#home"
                                                                onClick={(e) =>
                                                                    e.preventDefault()
                                                                }
                                                            >
                                                                <span
                                                                    id={`recent-invite-table-copy-link-${index}-mb`}
                                                                    className="gs-dashboard-invite-link"
                                                                >
                                                                    {copy !== index
                                                                        ? "Copy Invite Link"
                                                                        : "Copied"}
                                                                </span>
                                                            </a>
                                                        </CopyToClipboard>
                                                    )}
                                                </div>
                                                <div className="mt-2">
                                                    {renderResendButton(
                                                        invite.id,
                                                        `recent-invite-table-resend-invite-btn-${index}-mb`
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            ) : (
                                <div className="p-4 text-center">
                                    <span id="recent-invite-table-no-data-message-mb">
                                        No invites yet
                                    </span>
                                </div>
                            )
                        ) : (
                            <div id="recent-invite-table-data-loader-mb">
                                <InviteUserTableSkeletonMobile />
                            </div>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
};

export default InviteUserTable;
